<template>
  <div>
    <div class="d-flex justify-content-between ts-mb-3">
      <div>
        <h1 class="page-header ts-mb-0">{{ $t('invoice.pageTitle') }}</h1>
      </div>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('invoice.pageTitle'),
            active: true
          }
        ]"
      />
    </div>

    <ts-card>
      <ts-card-wrapper>
        <div class="list-toolbar">
          <div class="toolbar-left">
            <ts-button outline color="info" @click="createNew">
              {{ $t('add_new') }}
            </ts-button>
            <ts-button
              outline
              color="success"
              :waiting="exporting"
              @click.prevent="exportExcel"
            >
              <i class="fas fa-file-excel"></i>
              {{ $t('export') }}
            </ts-button>
          </div>
          <div class="toolbar-right">
            <a-input-search
              v-model="search"
              class="invoice-search"
              v-tooltip="$t('invoice.searchPlaceholder')"
              :placeholder="$t('invoice.searchPlaceholder')"
              @search="fetchData({ search, branch_id: branch_id })"
            />
            <a-button
              class="filter-button"
              icon="filter"
              @click="openFilterDrawer"
            >
              {{ $t('invoice.filter') }}
            </a-button>
          </div>
        </div>
      </ts-card-wrapper>

      <Spin size="large" fix v-if="generating"></Spin>
      <ts-loading-banner :loading="loading">
        <div class="table-shell">
          <Table
            :columns="columns"
            :data="resources"
            max-height="700"
            highlight-row
            size="small"
            :row-class-name="rowClassName"
          >
            <template slot-scope="{ row }" slot="branch">
              <span class="cell-text">{{ branchName(row) }}</span>
            </template>

            <template slot-scope="{ row }" slot="invoice_number">
              <span class="cell-text text-strong">{{
                row.inv_number || '-'
              }}</span>
            </template>

            <template slot-scope="{ row }" slot="invoice_date">
              <span class="cell-text">{{ row.inv_date || '-' }}</span>
            </template>

            <template slot-scope="{ row }" slot="student_name">
              <span class="student-name-cell">
                <span class="cell-text text-strong ts-text-blue-600">{{
                  studentName(row)
                }}</span>
                <span
                  class="student-pill"
                  :class="row.is_new_student ? 'student-new' : 'student-old'"
                >
                  {{
                    row.is_new_student ? $t('invoice.new') : $t('invoice.old')
                  }}
                </span>
              </span>
            </template>

            <template slot-scope="{ row }" slot="student_card">
              <span class="cell-text">{{ studentCard(row) }}</span>
            </template>

            <template slot-scope="{ row }" slot="student_gender">
              <span class="cell-text">{{ studentGender(row) }}</span>
            </template>

            <template slot-scope="{ row }" slot="student_phone">
              <span class="cell-text">{{ studentPhone(row) || '-' }}</span>
            </template>

            <template slot-scope="{ row }" slot="academic_program">
              <span class="cell-text" v-tooltip="academicProgramName(row)">
                {{ academicProgramName(row) }}
              </span>
            </template>

            <template slot-scope="{ row }" slot="sub_program">
              <span class="cell-text" v-tooltip="subProgramName(row)">{{
                subProgramName(row)
              }}</span>
            </template>

            <template slot-scope="{ row }" slot="academic_term">
              <span class="cell-text" v-tooltip="academicTerm(row)">{{
                academicTerm(row)
              }}</span>
            </template>

            <template slot-scope="{ row }" slot="grade">
              <span class="cell-text" v-tooltip="gradeName(row)">{{
                gradeName(row)
              }}</span>
            </template>

            <template slot-scope="{ row }" slot="class">
              <span class="cell-text" v-tooltip="className(row)">{{
                className(row)
              }}</span>
            </template>

            <template slot-scope="{ row }" slot="study_time">
              <span class="cell-text" v-tooltip="studyTime(row)">{{
                studyTime(row)
              }}</span>
            </template>

            <template slot-scope="{ row }" slot="total_amount">
              <div class="amount-text">
                {{ formatAmount(row.total_amount) }}
              </div>
            </template>

            <template slot-scope="{ row }" slot="discounted_amount">
              <div class="amount-text">
                {{ formatAmount(row.discounted_amount) }}
              </div>
            </template>

            <template slot-scope="{ row }" slot="amount_paid">
              <div class="amount-text">{{ formatAmount(row.amount_paid) }}</div>
            </template>

            <template slot-scope="{ row }" slot="amount_due">
              <div class="amount-text">{{ formatAmount(row.amount_due) }}</div>
            </template>

            <template slot-scope="{ row }" slot="next_payment_date">
              <span v-if="row.next_payment_date" class="date-pill">
                {{ row.next_payment_date }}
              </span>
              <span v-else>-</span>
            </template>

            <template slot-scope="{ row }" slot="remark">
              <span v-if="!row.remark">-</span>
              <span v-else-if="row.remark.length < 25">{{ row.remark }}</span>
              <span v-else v-tooltip="row.remark">
                {{ row.remark.substring(0, 25) + '...' }}
              </span>
            </template>

            <template slot-scope="{ row }" slot="status">
              <span
                class="status-pill"
                :class="isPaid(row) ? 'status-paid' : 'status-unpaid'"
              >
                {{ isPaid(row) ? $t('invoice.paid') : $t('invoice.unpaid') }}
              </span>
            </template>

            <template slot-scope="{ row }" slot="cashier">
              <span class="cell-text">{{ cashierName(row) }}</span>
            </template>

            <template slot-scope="{ row }" slot="action">
              <div class="action-cell">
                <a
                  href="#"
                  v-tooltip="$t('tooltip.show')"
                  class="action-link action-view"
                  @click.prevent="show(row)"
                >
                  <i class="fas fa-eye"></i>
                </a>
                <a
                  href="#"
                  v-tooltip="$t('pdf')"
                  class="action-link action-pdf"
                  @click.prevent="pdfView(row)"
                >
                  <i class="fas fa-file-pdf"></i>
                </a>
                <a
                  href="#"
                  v-tooltip="$t('delete')"
                  class="action-link action-delete"
                  :class="{ 'action-disabled': row._deleting }"
                  @click.prevent="deleteRecord(row)"
                >
                  <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                  <i class="fa fa-trash" v-else></i>
                </a>
              </div>
            </template>
          </Table>
        </div>
      </ts-loading-banner>

      <div class="list-pagination">
        <ts-total-list v-model="pagination" />
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>

      <pdf-preview
        v-model="pdf_view"
        :invoice-id="invoiceId"
        @cancel="value => (pdf_view = value)"
      />
    </ts-card>

    <a-drawer
      :title="$t('invoice.filter')"
      placement="right"
      :closable="false"
      :visible="visible"
      :zIndex="1021"
      width="360px"
      @close="onCloseDrawer"
    >
      <div class="filter-drawer-body">
        <div class="filter-field">
          <label>{{ $t('invoice.branch') }}</label>
          <a-select
            v-model="branch_id"
            class="ts-w-full"
            :options="branchs"
            showSearch
            mode="multiple"
            :filterOption="filterOption"
            :maxTagCount="1"
            :maxTagTextLength="17"
            :placeholder="$t('all')"
          />
        </div>

        <program-select @fetch="fetchData({ branch_id: branch_id })" />

        <div class="filter-field">
          <label>{{ $t('invoice.invoiceDate') }}</label>
          <a-range-picker
            class="ts-w-full"
            format="DD-MM-YYYY"
            :defaultValue="rangeDate"
            :defaultPickerValue="rangeDate"
            @change="dateRangeChange"
          >
            <template slot="renderExtraFooter"></template>
          </a-range-picker>
        </div>

        <div class="filter-field">
          <label>{{ $t('invoice.cashier') }}</label>
          <a-select
            v-model="created_by"
            class="ts-w-full"
            :options="cashiers"
            showSearch
            mode="multiple"
            :filterOption="filterOption"
            :maxTagCount="1"
            :maxTagTextLength="17"
            :placeholder="$t('all')"
          />
        </div>

        <div class="filter-field">
          <ts-checkbox
            v-model="isDeleted"
            @change="fetchData({ branch_id: branch_id })"
          >
            {{ $t('invoice.voided') }}
          </ts-checkbox>
        </div>
      </div>
    </a-drawer>
  </div>
</template>

<script>
import * as FileDownload from 'downloadjs'
import { Errors } from 'form-backend-validation'
import { debounce, trim } from 'lodash'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import PdfPreview from './component/pdf_preview'
import ProgramSelect from './component/program_select_box'
import Reason from './component/reason_delete'

export default {
  name: 'invoice',
  components: {
    PdfPreview,
    ProgramSelect
  },
  data() {
    return {
      generating: false,
      visible: false,
      loading: false,
      pdf_view: false,
      invoiceId: 0,
      branchs: [],
      cashiers: [],
      model: {
        void_reason: null
      },
      errors: new Errors(),
      rangeDate: [moment(), moment()],
      exporting: false
    }
  },
  created() {
    this.branch_id = [this.$store.state.authUser.default_branch_id]
    this.fetchData()
    this.getBranch()
    this.onFetchCashier()
  },
  computed: {
    ...mapState('invoice', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.invoice.search
      },
      set(newValue) {
        this.$store.commit('invoice/SET_SEARCH', newValue)
        this.$store.commit('invoice/RESET_CURRENT_PAGE')
      }
    },
    isDeleted: {
      get() {
        return this.$store.state.invoice.is_deleted
      },
      set(newValue) {
        this.$store.commit('invoice/SET_IS_DELETE', newValue)
        this.$store.commit('invoice/RESET_CURRENT_PAGE')
      }
    },
    is_external() {
      return this.$store.state.authUser.is_external
    },
    branch_id: {
      get() {
        return this.$store.state.invoice.branch_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_BRANCH_ID', newValue)
      }
    },
    created_by: {
      get() {
        return this.$store.state.invoice.created_by
      },
      set(newValue) {
        this.$store.commit('invoice/SET_CREATED_BY', newValue)
      }
    },
    columns() {
      return [
        { title: this.$t('invoice.branch'), slot: 'branch', minWidth: 150 },
        {
          title: this.$t('invoice.invoiceNumber'),
          slot: 'invoice_number',
          minWidth: 150
        },
        {
          title: this.$t('invoice.invoiceDate'),
          slot: 'invoice_date',
          width: 120
        },
        {
          title: this.$t('invoice.studentName'),
          slot: 'student_name',
          minWidth: 210
        },
        {
          title: this.$t('invoice.studentCard'),
          slot: 'student_card',
          width: 115
        },
        { title: this.$t('invoice.sex'), slot: 'student_gender', width: 80 },
        {
          title: this.$t('invoice.phoneNumber'),
          slot: 'student_phone',
          width: 120
        },
        {
          title: this.$t('invoice.academicProgram'),
          slot: 'academic_program',
          minWidth: 170
        },
        {
          title: this.$t('invoice.subProgram'),
          slot: 'sub_program',
          minWidth: 180
        },
        {
          title: this.$t('invoice.acTerm'),
          slot: 'academic_term',
          width: 135
        },
        { title: this.$t('invoice.grade'), slot: 'grade', minWidth: 130 },
        { title: this.$t('invoice.class'), slot: 'class', minWidth: 160 },
        {
          title: this.$t('invoice.studyTime'),
          slot: 'study_time',
          width: 125
        },
        {
          title: this.$t('invoice.totalAmount'),
          slot: 'total_amount',
          align: 'right',
          width: 125
        },
        {
          title: this.$t('invoice.discount'),
          slot: 'discounted_amount',
          align: 'right',
          width: 120
        },
        {
          title: this.$t('invoice.amountPaid'),
          slot: 'amount_paid',
          align: 'right',
          width: 125
        },
        {
          title: this.$t('invoice.dueAmount'),
          slot: 'amount_due',
          align: 'right',
          width: 120
        },
        {
          title: this.$t('invoice.status'),
          slot: 'status',
          align: 'center',
          width: 95
        },
        {
          title: this.$t('invoice.nextPaymentDate'),
          slot: 'next_payment_date',
          minWidth: 150
        },
        { title: this.$t('invoice.remark'), slot: 'remark', minWidth: 160 },
        { title: this.$t('invoice.cashier'), slot: 'cashier', minWidth: 130 },
        {
          title: this.$t('actions'),
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 145
        }
      ]
    }
  },
  methods: {
    ...mapActions('invoice', [
      'destroy',
      'invoiceFormView',
      'getInvoicePdfData'
    ]),
    createNew() {
      this.$router.push({ name: 'invoice-create' })
    },
    openFilterDrawer() {
      this.visible = true
    },
    onCloseDrawer() {
      this.visible = false
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },
    fetchData(attributes = {}) {
      this.loading = true
      this.$store
        .dispatch(
          'invoice/fetch',
          Object.assign({}, { branch_id: this.branch_id }, attributes)
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    getBranch() {
      this.$store
        .dispatch('user/getBranch', {
          filter: {
            user_branches: true
          }
        })
        .then(response => {
          this.branchs = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
    },
    deleteRecord(record) {
      if (record._deleting) return
      this.model.void_reason = null
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        content: h =>
          h(Reason, {
            props: {
              value: this.model,
              validate: this.errors,
              placeholder: this.$t('invoice.leaveReason')
            }
          }),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          return this.destroy({
            id: record.inv_id,
            data: this.model
          })
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              this.errors = new Errors(error.errors)
              return Promise.reject(error)
            })
        },
        onCancel() {}
      })
    },
    show(record) {
      this.$router.push({ name: 'invoice-show', params: { id: record.inv_id } })
    },
    pdfView(record) {
      this.invoiceId = record.inv_id
      this.pdf_view = true
    },
    dateRangeChange(dateRange) {
      if (!dateRange.length) return
      this.$store.commit(
        'invoice/SET_DATE_FROM',
        dateRange[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'invoice/SET_DATE_TO',
        dateRange[1].format('DD-MM-YYYY')
      )
      this.fetchData({ branch_id: this.branch_id })
    },
    onFetchCashier() {
      this.invoiceFormView({
        fnName: 'cashierMakeInvoice'
      }).then(response => {
        this.cashiers = response.data.cashierMakeInvoice
      })
    },
    exportExcel() {
      this.exporting = true
      const payload = {
        date_from: this.$store.state.invoice.date_from,
        date_to: this.$store.state.invoice.date_to,
        search: this.$store.state.invoice.search,
        branch_id: this.branch_id,
        filter: {
          is_deleted: this.$store.state.invoice.is_deleted,
          acp_id: this.$store.state.invoice.acp_id,
          acps_id: this.$store.state.invoice.acps_id,
          created_by: this.created_by
        }
      }
      this.$store
        .dispatch('invoice/exportExcel', payload)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
        })
        .finally(() => (this.exporting = false))
    },
    isPaid(row) {
      return row.is_paid === true || row.is_paid === 1 || row.is_paid === '1'
    },
    rowClassName(row) {
      return this.isVoided(row) ? 'invoice-voided-row' : ''
    },
    isVoided(row) {
      return (
        row.is_deleted === true ||
        row.is_deleted === 1 ||
        row.is_deleted === '1'
      )
    },
    branchName(row) {
      return row.branch
        ? row.branch.branch_name_en || row.branch.branch_name_kh || '-'
        : '-'
    },
    studentName(row) {
      return row.student
        ? row.student.full_name_en || row.student.full_name_kh || '-'
        : '-'
    },
    studentCard(row) {
      return row.student ? row.student.card_number || '-' : '-'
    },
    studentGender(row) {
      return row.student ? row.student.gender || '-' : '-'
    },
    studentPhone(row) {
      return row.student ? row.student.phone || '' : ''
    },
    academicProgramName(row) {
      const program =
        row.main_program || row.mainProgram || row.academic_program
      return program
        ? program.acp_name_foreign || program.acp_name_native || '-'
        : '-'
    },
    subProgramName(row) {
      return row.sub_program
        ? row.sub_program.acps_name_foreign ||
            row.sub_program.acps_name_native ||
            '-'
        : '-'
    },
    gradeName(row) {
      return row.grade
        ? row.grade.grade_name_foreign || row.grade.grade_name_native || '-'
        : '-'
    },
    academicTerm(row) {
      return row.academic_term ? row.academic_term.term_name || '-' : '-'
    },
    className(row) {
      return row.academic_class ? row.academic_class.class_name || '-' : '-'
    },
    studyTime(row) {
      return row.study_time || '-'
    },
    cashierName(row) {
      return row.user && row.user.employee
        ? row.user.employee.name_en || '-'
        : '-'
    },
    formatAmount(value) {
      const amount = Number(value || 0)
      return `$${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search, branch_id: this.branch_id })
    }, 500),
    branch_id: debounce(function(value) {
      this.fetchData({ branch_id: value })
    }, 500),
    is_external: debounce(function() {
      this.fetchData({ branch_id: this.branch_id })
    }, 500),
    created_by: debounce(function() {
      this.fetchData({ branch_id: this.branch_id })
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('invoice/RESET_STATE')
    next()
  }
}
</script>

<style scoped>
.list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  flex: 1;
  justify-content: flex-end;
}

.invoice-search {
  width: 100%;
  max-width: 360px;
}

.filter-button {
  white-space: nowrap;
}

.table-shell {
  margin-top: 12px;
  overflow: auto;
}

.text-strong {
  color: #111827;
  font-weight: 600;
}

.cell-text {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}

.meta-line {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 3px;
}

.student-pill,
.status-pill,
.date-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  margin-top: 0;
  white-space: nowrap;
}

.student-name-cell {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  gap: 6px;
  white-space: nowrap;
}

.student-new {
  background: #fff7ed;
  color: #c2410c;
}

.student-old {
  background: #eff6ff;
  color: #2563eb;
}

.amount-text {
  color: #111827;
  font-weight: 600;
  text-align: right;
}

.date-pill {
  background: #ffedd5;
  color: #c2410c;
}

.status-pill {
  min-width: 54px;
}

.status-paid {
  background: #dcfce7;
  color: #166534;
}

.status-unpaid {
  background: #fef3c7;
  color: #92400e;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.action-link {
  height: 26px;
  width: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.action-link:hover {
  text-decoration: none;
}

.action-view {
  color: #0f766e;
  background: #ccfbf1;
}

.action-pdf {
  color: #b45309;
  background: #fef3c7;
}

.action-delete {
  color: #dc2626;
  background: #fee2e2;
}

.action-disabled {
  opacity: 0.42;
  cursor: not-allowed;
}

.list-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
}

.filter-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.filter-field label {
  display: block;
  color: #374151;
  font-weight: 600;
  margin-bottom: 6px;
}

/deep/ .invoice-voided-row td {
  color: #b91c1c;
  background: #fff1f2 !important;
}

/deep/ .invoice-voided-row td:not(:last-child) {
  text-decoration: line-through;
}

/deep/ .invoice-voided-row .status-pill,
/deep/ .invoice-voided-row .action-cell {
  text-decoration: none;
}

/deep/ .ivu-table-cell {
  white-space: nowrap;
}
</style>
